import { trackEvent } from '../lib/analytics';
import React, { useRef, useState } from 'react';
import {
  AppleLogo,
  DownloadSimple,
  WindowsLogo
} from '@phosphor-icons/react';
import { DOWNLOADS, usePlatform } from '../lib/platform';
import MagneticLink from './MagneticLink';
import DownloadEvaluation from './DownloadEvaluation';
import DesktopSetupLink from './DesktopSetupLink';


const PlatformIcon = ({ platform }) => (
  platform.key === 'mac'
    ? <AppleLogo aria-hidden="true" weight="bold" />
    : <WindowsLogo aria-hidden="true" weight="bold" />
);

// The Free/Pro boundary, stated at the control that commits to the download.
//
// It was already on the page — in the stat tiles, the pricing card, the FAQ and /buy — but never
// beside a Download button. A cap read before downloading is a fair deal; the same cap met after
// twenty minutes of work on a real drawing feels like bait. Same fact, different feeling, and the
// difference is 200 pixels.
export const FREE_TIER_LIMIT_TEXT =
  'Free with no trial timer: markup, review, calibration, 3 hand-created measurements per document, and Revision Package viewing. Symbol Search, plugins, and Revision Package changes or publishing require Pro.';

// The one download control: a primary button for the visitor's detected OS with the requirements
// line under it, and the other platform one click away. On phones and unknown platforms it offers
// BOTH desktop builds instead of pushing a 228 MB DMG at a device that cannot run it. `source`
// feeds the download_click analytics event so the funnel can tell which section converts.
const DownloadCTA = ({ source, size = '', onDownload, tone = '', adjacentAction = null, compact = false, evaluationHref }) => {
  const [started, setStarted] = useState(null);
  const evaluationRef = useRef(null);
  const { primaryPlatform, otherPlatform, platformKnown } = usePlatform();

  const click = (platform) => {
    trackEvent('download_click', { source, platform: platform.key });
    setStarted(platform.key);
    if (evaluationRef.current) evaluationRef.current.open = true;
    if (onDownload) onDownload();
  };

  if (!platformKnown) {
    return (
      <div className="dl-cta">
        <div className="dl-primary-row">
          <div className="dl-both">
            {[DOWNLOADS.mac, DOWNLOADS.windows].map((platform) => (
              <MagneticLink key={platform.key} href={platform.url} className={`primary-btn ${size}`.trim()} download onClick={() => click(platform)}>
                <PlatformIcon platform={platform} /> Download for {platform.name}
              </MagneticLink>
            ))}
          </div>
          {adjacentAction}
        </div>
        <p className="dl-meta">PolyPDF runs on Mac and Windows desktops — pick the download for the machine you work on.</p>
        <p className="dl-terms">{compact ? <>Free markup and review, plus 3 measurements per document. <a href="#pricing">Compare Free and Pro</a>.</> : FREE_TIER_LIMIT_TEXT}</p>
        <DesktopSetupLink source={source} />
        <DownloadEvaluation detailsRef={evaluationRef} requestedPlatform={started} source={source} tone={tone} guideHref={evaluationHref} />
      </div>
    );
  }

  return (
    <div className="dl-cta">
      <div className="dl-primary-row">
        <MagneticLink
          href={primaryPlatform.url}
          className={`primary-btn ${size}`.trim()}
          download
          onClick={() => click(primaryPlatform)}
        >
          <DownloadSimple aria-hidden="true" weight="bold" /> Download free for {primaryPlatform.name}
        </MagneticLink>
        {adjacentAction}
      </div>
      <p className="dl-meta">
        {primaryPlatform.requirements}
        <span className="dl-meta-sep" aria-hidden="true">·</span>
        <a href={otherPlatform.url} className="dl-alt" download onClick={() => click(otherPlatform)}>
          <PlatformIcon platform={otherPlatform} /> Also on {otherPlatform.name}
        </a>
      </p>
      <p className="dl-terms">{compact ? <>Free markup and review, plus 3 measurements per document. <a href="#pricing">Compare Free and Pro</a>.</> : FREE_TIER_LIMIT_TEXT}</p>
      <DesktopSetupLink source={source} />
      <DownloadEvaluation detailsRef={evaluationRef} requestedPlatform={started} source={source} tone={tone} guideHref={evaluationHref} />
    </div>
  );
};

// Explicit two-button variant for pages where the visitor may be grabbing the app for a DIFFERENT
// machine than the one they are browsing on (the post-purchase account page, most of all).
export const DownloadBoth = ({ source }) => (
  <div className="dl-both">
    {[DOWNLOADS.mac, DOWNLOADS.windows].map((platform) => (
      <a
        key={platform.key}
        href={platform.url}
        className="secondary-btn"
        download
        onClick={() => trackEvent('download_click', { source, platform: platform.key })}
      >
        <PlatformIcon platform={platform} /> Download for {platform.name}
      </a>
    ))}
  </div>
);

export default DownloadCTA;
